import { useState } from 'react'
import { Copy, Check, Plus, Pencil, Trash2, Instagram } from 'lucide-react'
import { Button, Input, Modal } from '@/ui'
import styles from './Memos.module.css'

interface Memo {
  id: string
  instagramAccount: string
  paCode: string
  colorIndex: number
}

const CARD_COLORS = [
  { bg: '#D4E8FF', chip: '#A8D0F5' }, // 파란
  { bg: '#FFE4CC', chip: '#F5C89A' }, // 주황
  { bg: '#E8D5FF', chip: '#CCA8F5' }, // 보라
  { bg: '#C8F0E0', chip: '#8FD9BB' }, // 초록
]

const STORAGE_KEY = 'fiket-admin-memos'

function loadMemos(): Memo[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]')
  } catch {
    return []
  }
}

function saveMemos(memos: Memo[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(memos))
}

export function Memos() {
  const [memos, setMemos] = useState<Memo[]>(loadMemos)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Memo | null>(null)
  const [form, setForm] = useState({ instagramAccount: '', paCode: '' })
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const openAdd = () => {
    setEditing(null)
    setForm({ instagramAccount: '', paCode: '' })
    setModalOpen(true)
  }

  const openEdit = (memo: Memo) => {
    setEditing(memo)
    setForm({ instagramAccount: memo.instagramAccount, paCode: memo.paCode })
    setModalOpen(true)
  }

  const handleSave = () => {
    const account = form.instagramAccount.trim()
    const code = form.paCode.trim()
    if (!account && !code) return

    let next: Memo[]
    if (editing) {
      next = memos.map((m) =>
        m.id === editing.id ? { ...editing, instagramAccount: account, paCode: code } : m,
      )
    } else {
      next = [
        ...memos,
        {
          id: crypto.randomUUID(),
          instagramAccount: account,
          paCode: code,
          colorIndex: memos.length % CARD_COLORS.length,
        },
      ]
    }
    setMemos(next)
    saveMemos(next)
    setModalOpen(false)
  }

  const handleDelete = (id: string) => {
    const next = memos.filter((m) => m.id !== id)
    setMemos(next)
    saveMemos(next)
    setDeleteConfirmId(null)
  }

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 1800)
  }

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <button className={styles.addBtn} onClick={openAdd}>
          <Plus size={15} />
          메모 추가
        </button>
      </div>

      {memos.length === 0 && (
        <div className={styles.empty}>
          <p>저장된 메모가 없습니다.</p>
          <p>추가 버튼으로 인스타 계정과 PA 코드를 기록해보세요.</p>
        </div>
      )}

      <div className={styles.grid}>
        {memos.map((memo) => {
          const color = CARD_COLORS[memo.colorIndex % CARD_COLORS.length]
          return (
            <div key={memo.id} className={styles.card}>
              {/* 컬러 헤더 영역 */}
              <div className={styles.cardHeader} style={{ background: color.bg }}>
                <div className={styles.cardHeaderActions}>
                  <button className={styles.iconBtn} onClick={() => openEdit(memo)} title="편집">
                    <Pencil size={13} />
                  </button>
                  <button
                    className={styles.iconBtn}
                    onClick={() => setDeleteConfirmId(memo.id)}
                    title="삭제"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className={styles.instagramRow}>
                  <Instagram size={16} className={styles.instagramIcon} />
                  <span className={styles.instagramLabel}>인스타 계정</span>
                </div>
                <p className={styles.instagramValue}>
                  {memo.instagramAccount || <span className={styles.placeholder}>미입력</span>}
                </p>
              </div>

              {/* 하단 PA 코드 영역 */}
              <div className={styles.cardBody}>
                <p className={styles.paLabel}>PA 코드</p>
                <div className={styles.paRow}>
                  <span className={styles.paChip} style={{ background: color.chip }}>
                    {memo.paCode || <span className={styles.placeholder}>미입력</span>}
                  </span>
                  {memo.paCode && (
                    <button
                      className={styles.copyBtn}
                      onClick={() => handleCopy(memo.paCode, memo.id)}
                      title="복사"
                    >
                      {copiedId === memo.id ? (
                        <>
                          <Check size={13} />
                          복사됨
                        </>
                      ) : (
                        <>
                          <Copy size={13} />
                          복사
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* 추가/편집 모달 */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? '메모 편집' : '메모 추가'}
        size="sm"
      >
        <div className={styles.modalContent}>
          <Input
            label="인스타 계정"
            placeholder="@username"
            value={form.instagramAccount}
            onChange={(e) => setForm((f) => ({ ...f, instagramAccount: e.target.value }))}
          />
          <Input
            label="PA 코드"
            placeholder="코드를 입력하세요"
            value={form.paCode}
            onChange={(e) => setForm((f) => ({ ...f, paCode: e.target.value }))}
          />
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              취소
            </Button>
            <Button onClick={handleSave}>저장</Button>
          </div>
        </div>
      </Modal>

      {/* 삭제 확인 모달 */}
      <Modal
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="메모 삭제"
        size="sm"
      >
        <div className={styles.modalContent}>
          <p style={{ color: 'var(--ds-color-text-secondary)', fontSize: 'var(--ds-font-size-sm)' }}>
            이 메모를 삭제할까요? 되돌릴 수 없습니다.
          </p>
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setDeleteConfirmId(null)}>
              취소
            </Button>
            <Button variant="danger" onClick={() => handleDelete(deleteConfirmId!)}>
              삭제
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
